import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	GuaranteeCard,
	NotesCard,
	OrderTitleBar,
	PaymentSummaryCard,
	ProviderCard,
	StatusTimeline,
	SupportCard,
} from "../components/order-detail";
import { Layout } from "../layout/Layout";
import { Upload, X } from "lucide-react";
import { ReportReasonModal } from "../components/modals/ReportReasonModal";

import type { OrderDetailData, QuotationCardData } from "../core/types/order-detail";
import type { PaymentItem } from "../core/types/order-detail";
import type { Order } from "../core/types/order";
import { useOrder } from "../hooks/services";
import { StatusOrder } from "../core/types/services/order";
import { normalizeOrderStatusLabel } from "../core/utils/orderStatus";
import { useAuthSessionContext } from "../components/routing/auth-session-store";
import { useFetch } from "../hooks/useFetch";
import { useCreatePayment, usePaymentExchangeRate } from "../hooks/services/usePayments";
import { useMethodPayments } from "../hooks/control/useMethodPayments";
import {
	buildInlinePaymentSupport,
	resolvePaymentSupportSrc,
} from "../core/utils/payment-support";
import { getImageUri } from "../core/utils/imageLoad";

const formatDate = (dateStr?: string | null) => {
	if (!dateStr) return "N/A";
	return new Date(dateStr).toLocaleDateString("es-ES", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	});
};

const formatDateOnly = (dateStr?: string | null) => {
	if (!dateStr) return "N/A";
	const parsed = new Date(dateStr);
	if (Number.isNaN(parsed.getTime())) return "N/A";
	return parsed.toLocaleDateString("es-VE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
};

const formatStartHour = (hourValue?: string | null, dateValue?: string | null) => {
	const to12HourLabel = (hour24: number, minutes: number) => {
		const period = hour24 >= 12 ? "PM" : "AM";
		const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
		return `${String(hour12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
	};

	const normalizedHour = (hourValue ?? "").trim();
	if (normalizedHour) {
		const match24h = normalizedHour.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
		if (match24h) {
			const hour24 = Number(match24h[1]);
			const minutes = Number(match24h[2]);
			if (
				Number.isFinite(hour24) &&
				Number.isFinite(minutes) &&
				hour24 >= 0 &&
				hour24 <= 23 &&
				minutes >= 0 &&
				minutes <= 59
			) {
				return to12HourLabel(hour24, minutes);
			}
		}

		const match12h = normalizedHour.match(/^(\d{1,2}):(\d{2})(?:\s*)(AM|PM)$/i);
		if (match12h) {
			const hour12 = Number(match12h[1]);
			const minutes = Number(match12h[2]);
			const period = match12h[3].toUpperCase();
			if (
				Number.isFinite(hour12) &&
				Number.isFinite(minutes) &&
				hour12 >= 1 &&
				hour12 <= 12 &&
				minutes >= 0 &&
				minutes <= 59
			) {
				return `${String(hour12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
			}
		}

		const parsedHourDate = new Date(normalizedHour);
		if (!Number.isNaN(parsedHourDate.getTime())) {
			return parsedHourDate.toLocaleTimeString("es-VE", {
				hour: "2-digit",
				minute: "2-digit",
				hour12: true,
			});
		}
	}

	if (!dateValue) return "N/A";
	const parsedDate = new Date(dateValue);
	if (Number.isNaN(parsedDate.getTime())) return "N/A";
	return parsedDate.toLocaleTimeString("es-VE", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	});
};

const extractStructuredScheduleTime = (raw?: string | null): string | null => {
	if (!raw) return null;
	const marker = "SERVIX_REQUEST_V1::";
	if (!raw.startsWith(marker)) return null;
	const encoded = raw.slice(marker.length).trim();
	if (!encoded) return null;

	try {
		const parsed = JSON.parse(encoded) as {
			format?: string;
			schedule?: { time?: string };
		};
		if (parsed?.format !== "SERVIX_REQUEST_V1") return null;
		const time = parsed.schedule?.time?.trim();
		return time && time.length > 0 ? time : null;
	} catch {
		return null;
	}
};

const extractRequestScheduleFromPayload = (payload: unknown) => {
	if (!payload || typeof payload !== "object") {
		return { hour: null, date: null };
	}

	const root = payload as Record<string, unknown>;
	const requestNode =
		root.request && typeof root.request === "object"
			? (root.request as Record<string, unknown>)
			: root;

	const hour =
		typeof requestNode.hour_request === "string" ? requestNode.hour_request : null;
	const date =
		typeof requestNode.date_request === "string" ? requestNode.date_request : null;

	return { hour, date };
};

const DEFAULT_AVATAR =
	"https://images.unsplash.com/photo-1599566150163-29194dcabd9c?auto=format&fit=crop&w=240&q=80";

export function ClientInfoPedido() {
	const navigate = useNavigate();
	const location = useLocation() as {
		state?: {
			order?: Order;
			orderId?: number | string;
			orderData?: { id?: number | string };
		};
	};
	const { id: paramId } = useParams();
	const { session } = useAuthSessionContext();

	const rawOrderId =
		location.state?.orderId ??
		location.state?.orderData?.id ??
		location.state?.order?.rawId ??
		location.state?.order?.id ??
		paramId;
	const orderId = Number(rawOrderId);
	const hasValidOrderId = Number.isFinite(orderId) && orderId > 0;

	const isClient = session?.user?.type_user === "Cliente";
	const isProvider = session?.user?.type_user === "Proveedor";
	const isEndUser =
		session?.user?.type_user === "Cliente" ||
		session?.user?.type_user === "Proveedor";
	const shouldHideProviderContactAndChat = isEndUser;

	const {
		data: apiOrder,
		loading,
		error,
		markOrderSupport,
		claimOrderWarranty,
		actionLoading,
		actionError,
		execute: refetchOrder,
	} = useOrder(hasValidOrderId ? orderId : 0);
	const { execute: fetchRequestById } = useFetch<unknown>({
		endpoint: "/requests/0",
		method: "GET",
		auto: false,
	});

	// Payment form state
	const [showPaymentForm, setShowPaymentForm] = useState(false);
	const [paymentAmount, setPaymentAmount] = useState("");
	const [fromAccount, setFromAccount] = useState("");
	const [toAccount, setToAccount] = useState("");
	const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
	const [paymentScreenshot, setPaymentScreenshot] = useState<File | null>(null);
	const [screenshotPreview, setScreenshotPreview] = useState("");
	const [paymentError, setPaymentError] = useState("");
	const [showReportModal, setShowReportModal] = useState(false);
	const [reportReason, setReportReason] = useState("");
	const [reportReasonError, setReportReasonError] = useState("");
	const [nowTimestamp] = useState(() => Date.now());
	const [requestScheduleOverride, setRequestScheduleOverride] = useState<{
		hour: string | null;
		date: string | null;
	}>({
		hour: null,
		date: null,
	});
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const { create: createPayment, loading: creatingPayment } = useCreatePayment();
	const { data: exchangeRateData } = usePaymentExchangeRate();
	const { data: methodsData } = useMethodPayments(1, 50);

	const methods = useMemo(() => {
		if (!methodsData) return [];
		const raw = Array.isArray(methodsData)
			? methodsData
			: (methodsData as Record<string, unknown>)?.items
				? ((methodsData as Record<string, unknown>).items as Array<
						Record<string, unknown>
					>)
				: [];
		return raw
			.filter((m) => m && typeof m === "object" && "id" in m)
			.map((m) => ({
				id: Number((m as Record<string, unknown>).id),
				name_method: String(
					(m as Record<string, unknown>).name_method ?? "Metodo",
				),
			}));
	}, [methodsData]);

	const providerAccountsList = useMemo(() => {
		return [] as Array<{
			id: number;
			account_number: string;
			holder_name: string;
			method_id: number;
		}>;
	}, []);

	const handleMarkSupport = useCallback(async () => {
		if (!apiOrder || !isEndUser) return;
		if (
			apiOrder.status_order === StatusOrder.SUPPORT ||
			apiOrder.status_order === StatusOrder.CANCELLED
		) {
			window.alert(
				"Este pedido no puede marcarse para soporte en su estado actual.",
			);
			return;
		}
		setReportReason("");
		setReportReasonError("");
		setShowReportModal(true);
	}, [apiOrder, isEndUser]);

	const handleCloseReportModal = useCallback(() => {
		if (actionLoading) return;
		setShowReportModal(false);
		setReportReason("");
		setReportReasonError("");
	}, [actionLoading]);

	const handleSubmitSupportReport = useCallback(async () => {
		const trimmedReason = reportReason.trim();
		if (!trimmedReason) {
			setReportReasonError("Debes indicar un motivo del reporte.");
			return;
		}

		await markOrderSupport(trimmedReason);
		handleCloseReportModal();
	}, [reportReason, markOrderSupport, handleCloseReportModal]);

	const handleClaimWarranty = useCallback(async () => {
		if (!apiOrder || !isClient || apiOrder.status_order !== StatusOrder.WARRANTY)
			return;
		const confirmed = window.confirm(
			"¿Deseas enviar el reclamo de garantia para este pedido?",
		);
		if (!confirmed) return;
		await claimOrderWarranty("Reclamo de garantia desde cliente");
	}, [apiOrder, claimOrderWarranty, isClient]);

	const handleOpenChat = useCallback(() => {
		navigate("/chat-expanded", { state: { orderId } });
	}, [navigate, orderId]);
	const handleGoBack = useCallback(() => {
		navigate("/user-profile", { state: { initialTab: "orders" } });
	}, [navigate]);

	const handlePay = useCallback(() => {
		setShowPaymentForm(true);
		setPaymentError("");
	}, []);

	const handleRetryPaymentRegistration = useCallback(() => {
		navigate("/registro/servicio", {
			state: {
				orderId,
				resetPaymentForm: true,
				paymentFlowToken: Date.now(),
			},
		});
	}, [navigate, orderId]);

	const handleScreenshotChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;
			const allowed = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
			if (!allowed.includes(file.type)) {
				setPaymentError("Solo se permiten imagenes (JPG, PNG, WEBP).");
				return;
			}
			if (file.size > 5 * 1024 * 1024) {
				setPaymentError("La imagen no puede superar los 5MB.");
				return;
			}
			setPaymentScreenshot(file);
			setScreenshotPreview(URL.createObjectURL(file));
			setPaymentError("");
		},
		[],
	);

	const clearScreenshot = useCallback(() => {
		if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
		setPaymentScreenshot(null);
		setScreenshotPreview("");
		if (fileInputRef.current) fileInputRef.current.value = "";
	}, [screenshotPreview]);

	const handleSubmitPayment = useCallback(async () => {
		setPaymentError("");
		const amount = Number(paymentAmount);
		if (!amount || amount <= 0) {
			setPaymentError("Ingresa un monto valido.");
			return;
		}
		if (!fromAccount.trim()) {
			setPaymentError("Ingresa la cuenta de origen.");
			return;
		}
		if (!toAccount.trim()) {
			setPaymentError("Ingresa la cuenta destino.");
			return;
		}
		if (!selectedMethodId) {
			setPaymentError("Selecciona un metodo de pago.");
			return;
		}

		try {
			let paymentSupportValue: string | undefined;
			if (paymentScreenshot) {
				const inlineSupport = await buildInlinePaymentSupport(paymentScreenshot);
				if (!inlineSupport) {
					setPaymentError(
						"No se pudo procesar la captura de pantalla. Usa una imagen más liviana e intenta de nuevo.",
					);
					return;
				}
				paymentSupportValue = inlineSupport;
			}

			await createPayment(
				{
					order_id: orderId,
					method_id: selectedMethodId,
					amount_payment: amount,
					from_account: fromAccount.trim(),
					to_account: toAccount.trim(),
					...(paymentSupportValue
						? { payment_support: paymentSupportValue }
						: {}),
				},
				paymentScreenshot ? { payment_support: paymentScreenshot } : undefined,
			);
			setShowPaymentForm(false);
			setPaymentAmount("");
			setFromAccount("");
			setToAccount("");
			setSelectedMethodId(null);
			clearScreenshot();
			void refetchOrder();
		} catch (err) {
			setPaymentError(
				err instanceof Error
					? err.message
					: "No se pudo registrar el pago. Intenta de nuevo.",
			);
		}
	}, [
		paymentAmount,
		fromAccount,
		toAccount,
		selectedMethodId,
		orderId,
		paymentScreenshot,
		createPayment,
		clearScreenshot,
		refetchOrder,
	]);

	const latestRejectedPayment = useMemo(() => {
		if (!apiOrder?.payments?.length) return null;
		return (
			[...apiOrder.payments]
				.filter((payment) => payment.payment_status === "Rechazado")
				.sort((a, b) => b.id - a.id)[0] ?? null
		);
	}, [apiOrder]);

	const providerOwnsOrder = useMemo(() => {
		if (!isProvider || !apiOrder) return true;

		const providerUser = apiOrder.providers.users as
			| (typeof apiOrder.providers.users & {
					id?: number | string | null;
			  })
			| undefined;

		const providerUserId = Number(providerUser?.id);
		if (Number.isFinite(providerUserId) && providerUserId > 0) {
			return providerUserId === session?.user?.id;
		}

		const providerEmail = providerUser?.email?.trim().toLowerCase() ?? "";
		const sessionEmail = session?.user?.email?.trim().toLowerCase() ?? "";
		if (providerEmail && sessionEmail) {
			return providerEmail === sessionEmail;
		}

		return false;
	}, [apiOrder, isProvider, session?.user?.email, session?.user?.id]);

	const requestIdFromOrder = useMemo(() => {
		if (!apiOrder) return null;

		const directRequestId = Number(
			(apiOrder as { request_id?: unknown }).request_id ?? 0,
		);
		if (Number.isFinite(directRequestId) && directRequestId > 0) {
			return directRequestId;
		}

		const nestedRequestId = Number(apiOrder.requests?.id ?? 0);
		if (Number.isFinite(nestedRequestId) && nestedRequestId > 0) {
			return nestedRequestId;
		}

		const quotations = Array.isArray(apiOrder.quotations) ? apiOrder.quotations : [];
		for (const quotation of quotations) {
			const quotationRequestId = Number(quotation?.requests?.id ?? 0);
			if (Number.isFinite(quotationRequestId) && quotationRequestId > 0) {
				return quotationRequestId;
			}
		}

		return null;
	}, [apiOrder]);

	useEffect(() => {
		let cancelled = false;

		if (!requestIdFromOrder) {
			setRequestScheduleOverride({ hour: null, date: null });
			return;
		}

		const loadRequestSchedule = async () => {
			try {
				const payload = await fetchRequestById({
					endpoint: `/requests/${requestIdFromOrder}`,
				});
				if (cancelled) return;
				setRequestScheduleOverride(extractRequestScheduleFromPayload(payload));
			} catch {
				if (cancelled) return;
				setRequestScheduleOverride({ hour: null, date: null });
			}
		};

		void loadRequestSchedule();

		return () => {
			cancelled = true;
		};
	}, [fetchRequestById, requestIdFromOrder]);

	const orderDetail = useMemo(() => {
		if (!apiOrder) return null;

		const quotations = Array.isArray(apiOrder.quotations) ? apiOrder.quotations : [];
		const latestQuotation = quotations.length
			? [...quotations].sort((a, b) => b.id - a.id)[0]
			: null;
		const requestData = apiOrder.requests ?? quotations[0]?.requests;
		const serviceProvider = requestData?.services_providers;
		const quotationItems = latestQuotation?.quotation_items || [];
		const effectiveOrderPrice =
			Number(apiOrder.order_price ?? 0) > 0
				? Number(apiOrder.order_price ?? 0)
				: Number(apiOrder.order_price_aux ?? 0);
		const requestDateValue = apiOrder.date_initial_order ?? apiOrder.created_at;
		const startDateValue =
			apiOrder.estimated_date_completion ?? apiOrder.date_initial_order;
		const requestHourFromDescription = extractStructuredScheduleTime(
			requestData?.description_request,
		);
		const requestHourValue =
			requestData?.hour_request ??
			requestScheduleOverride.hour ??
			quotations
				.map((quotation) => quotation?.requests?.hour_request)
				.find(
					(hour): hour is string =>
						typeof hour === "string" && hour.trim().length > 0,
				) ??
			requestHourFromDescription ??
			null;
		const requestScheduledDateValue =
			requestData?.date_request ??
			requestScheduleOverride.date ??
			quotations
				.map((quotation) => quotation?.requests?.date_request)
				.find(
					(date): date is string =>
						typeof date === "string" && date.trim().length > 0,
				) ??
			null;
		const startHourValue = requestHourValue
			? formatStartHour(requestHourValue, requestScheduledDateValue)
			: "N/A";

		const serviceName =
			serviceProvider?.name_service ||
			requestData?.title_request ||
			"Servicio no disponible";

		const categoryName =
			serviceProvider?.categories_services_providers?.[0]?.categories_providers
				.categories_services.name_category ||
			apiOrder.providers.categories_providers?.[0]?.categories_services
				?.name_category ||
			"Sin categoria";

		const requestServiceAmount =
			requestData?.proposed_price_request ||
			serviceProvider?.base_price ||
			effectiveOrderPrice ||
			0;

		const latestQuotationSubtotal = quotationItems.reduce(
			(sum, item) => sum + (item.subtotal_item || 0),
			0,
		);
		const latestQuotationTotal =
			latestQuotation?.proposed_price || latestQuotationSubtotal || 0;

		const breakdownItems: PaymentItem[] = latestQuotation
			? [
					{
						label: `Cotizacion #${latestQuotation.id}`,
						amount: `$${latestQuotationTotal}`,
						tone: "muted",
					},
					...quotationItems.map((item) => ({
						label: item.description_item,
						amount: `$${item.subtotal_item}`,
					})),
				]
			: [{ label: serviceName, amount: `$${requestServiceAmount}` }];

		const paymentItems: PaymentItem[] = [
			...breakdownItems,
			...(apiOrder.payments?.map((p, i) => {
				const tone: PaymentItem["tone"] =
					p.payment_status === "Rechazado"
						? "negative"
						: p.payment_status === "Aprobado"
							? "positive"
							: undefined;
				return {
					label: `Pago ${i + 1} (${p.payment_status})`,
					amount: `$${p.amount_payment}`,
					tone,
				};
			}) || []),
		];

		const statusOrder = apiOrder.status_order;

		const FLOW: StatusOrder[] = [
			StatusOrder.PENDING,
			StatusOrder.WAITING,
			StatusOrder.IN_PROGRESS,
			StatusOrder.COMPLETED,
		];

		const FLOW_LABELS: Record<string, string> = {
			[StatusOrder.PENDING]: "Dialogando",
			[StatusOrder.WAITING]: "En Espera",
			[StatusOrder.IN_PROGRESS]: "En Progreso",
			[StatusOrder.COMPLETED]: "Completado",
			[StatusOrder.PAID]: "Pagado",
			[StatusOrder.SUPPORT]: "Soporte",
			[StatusOrder.WARRANTY]: "Garantia",
			[StatusOrder.CANCELLED]: "Cancelado",
		};

		const FLOW_DATES: Record<string, string | null | undefined> = {
			[StatusOrder.PENDING]: apiOrder.date_initial_order,
			[StatusOrder.WAITING]: apiOrder.date_waiting_order,
			[StatusOrder.IN_PROGRESS]: apiOrder.date_in_progress_order,
			[StatusOrder.COMPLETED]: apiOrder.date_completion_order,
			[StatusOrder.PAID]:
				apiOrder.date_paid_order ??
				apiOrder.payments?.find((p) => p.date_approval_payment)
					?.date_approval_payment ??
				null,
			[StatusOrder.SUPPORT]: apiOrder.date_support_order,
			[StatusOrder.WARRANTY]: apiOrder.date_guarantee_order,
			[StatusOrder.CANCELLED]: apiOrder.date_cancelation_order,
		};

		const effectiveFlowStatus =
			statusOrder === StatusOrder.PAID ? StatusOrder.COMPLETED : statusOrder;
		const currentFlowIndex = FLOW.indexOf(effectiveFlowStatus);
		const specialStatuses: StatusOrder[] = [
			StatusOrder.SUPPORT,
			StatusOrder.WARRANTY,
			StatusOrder.CANCELLED,
			StatusOrder.PAID,
		];
		const isSpecialState = specialStatuses.includes(statusOrder);

		const terminalStatuses: StatusOrder[] = [StatusOrder.COMPLETED, StatusOrder.PAID];
		const isTerminal = terminalStatuses.includes(statusOrder);

		const flowSteps = FLOW.map((s, i) => ({
			label: FLOW_LABELS[s],
			date: formatDate(FLOW_DATES[s]),
			state: (currentFlowIndex > i || (currentFlowIndex === i && isTerminal)
				? "done"
				: currentFlowIndex === i
					? "current"
					: "pending") as "done" | "current" | "pending",
		}));

		const specialStep = isSpecialState
			? [
					{
						label: FLOW_LABELS[statusOrder],
						date: formatDate(FLOW_DATES[statusOrder]),
						state: "current" as const,
					},
				]
			: [];

		const hasApprovedPayment = apiOrder.payments?.some(
			(p) => p.payment_status === "Aprobado",
		);
		const hasPendingPayment = apiOrder.payments?.some(
			(p) => p.payment_status === "Pendiente",
		);
		const hasRejectedPayment = Boolean(latestRejectedPayment);
		const paymentEnabledStatuses: StatusOrder[] = [
			StatusOrder.PENDING,
			StatusOrder.WAITING,
		];
		const canRegisterByStatus = paymentEnabledStatuses.includes(statusOrder);
		const canRetryRejectedPayment =
			hasRejectedPayment &&
			statusOrder !== StatusOrder.CANCELLED &&
			statusOrder !== StatusOrder.COMPLETED &&
			statusOrder !== StatusOrder.PAID;
		const canPay =
			isClient &&
			!hasApprovedPayment &&
			!hasPendingPayment &&
			(canRegisterByStatus || canRetryRejectedPayment);
		const warrantyExpirationAt = apiOrder.date_expiration_guarantee
			? new Date(apiOrder.date_expiration_guarantee)
			: null;
		const hasWarrantyExpiration =
			warrantyExpirationAt instanceof Date &&
			!Number.isNaN(warrantyExpirationAt.getTime());
		const isWarrantyWindowActive = hasWarrantyExpiration
			? warrantyExpirationAt.getTime() >= nowTimestamp
			: statusOrder === StatusOrder.COMPLETED;
		const isCompletedOrder = statusOrder === StatusOrder.COMPLETED;
		const canClaimWarranty = isClient && isCompletedOrder && isWarrantyWindowActive;
		const canMarkSupport =
			isEndUser &&
			statusOrder !== StatusOrder.SUPPORT &&
			statusOrder !== StatusOrder.CANCELLED &&
			(!isCompletedOrder || isWarrantyWindowActive);

		const notesFromQuotationItems = quotationItems.length
			? `Items cotizados: ${quotationItems
					.map((item) => `${item.description_item} ($${item.subtotal_item})`)
					.join(" | ")}`
			: null;
		const rejectionPaymentNote = latestRejectedPayment
			? `Conciliación de pago rechazada: ${latestRejectedPayment.rejection_reason_payment || "Sin motivo indicado."}`
			: null;

		const mappedOrderDetail: OrderDetailData = {
			title: "Detalles del Pedido",
			orderCode: apiOrder.code_order,
			statusBadge: apiOrder.status_order,

			statusSteps: [...flowSteps, ...specialStep],

			service: {
				servicio: serviceName,
				categoria: categoryName,
				descripcion: requestData?.description_request || "",
			},

			provider: {
				id: apiOrder.providers.id,
				name: apiOrder.providers.company_name,
				rating: 5.0,
				reviews: 0,
				avatarUrl:
					getImageUri(apiOrder.providers.users.image_profile) || DEFAULT_AVATAR,
				phone: shouldHideProviderContactAndChat
					? ""
					: apiOrder.providers.users.phone,
				email: shouldHideProviderContactAndChat
					? ""
					: apiOrder.providers.users.email,
				verified: true,
				onReviewChat: shouldHideProviderContactAndChat
					? undefined
					: handleOpenChat,
			},

			payment: {
				items: paymentItems,
				totalLabel: "Total",
				totalAmount: `$${latestQuotationTotal || requestServiceAmount || effectiveOrderPrice}`,
				actionLabel: canPay
					? hasRejectedPayment
						? "Volver a registrar pago"
						: "Registrar Pago"
					: hasPendingPayment
						? "Pago en revision"
						: undefined,
				onAction: canPay
					? hasRejectedPayment
						? handleRetryPaymentRegistration
						: handlePay
					: undefined,
				actionDisabled: actionLoading || !canPay,
				actionLoading,
			},

			meta: [
				{ label: "Numero de Pedido", value: apiOrder.code_order },
				{ label: "Fecha", value: formatDateOnly(requestDateValue) },
				{ label: "Servicio", value: serviceName },
				{ label: "Categoria", value: categoryName },
				{
					label: "Fecha Inicio",
					value: formatDateOnly(startDateValue),
					separator: true,
				},
				{
					label: "Ubicacion",
					value: `${apiOrder.addresses.street_address}. ${apiOrder.addresses.reference_address}`,
				},
				{
					label: "Hora",
					value: startHourValue,
				},
				{
					label: "Estado",
					value: normalizeOrderStatusLabel(apiOrder.status_order),
				},
			],

			guarantee: {
				points: [
					{
						text: apiOrder.is_guarantee_used
							? "Garantia activada para este servicio."
							: "Garantia disponible.",
					},
					{
						text: apiOrder.date_expiration_guarantee
							? `Vence el: ${formatDate(apiOrder.date_expiration_guarantee)}`
							: "Servicio garantizado por soporte.",
					},
				],
				actionLabel: canClaimWarranty ? "Reclamar Garantia" : undefined,
				onAction: canClaimWarranty ? handleClaimWarranty : undefined,
				actionDisabled: actionLoading,
				actionLoading,
			},

			notes: {
				text:
					[
						apiOrder.cancelation_reason,
						apiOrder.status_reason,
						apiOrder.reason_culmination,
						rejectionPaymentNote,
						notesFromQuotationItems,
					]
						.filter(Boolean)
						.join(" | ") || "Sin notas adicionales del sistema.",
			},

			quotation: latestQuotation
				? ({
						id: latestQuotation.id,
						proposedPrice: latestQuotation.proposed_price,
						accepted: latestQuotation.status_quotation,
						items: quotationItems.map((item) => ({
							description: item.description_item,
							subtotal: item.subtotal_item,
						})),
					} satisfies QuotationCardData)
				: null,

			help: {
				title: "Necesitas ayuda con este pedido?",
				description: "Contacta al soporte indicando el codigo del pedido.",
				actionLabel: canMarkSupport
					? "Reportar un problema"
					: "Soporte no disponible",
				onAction: canMarkSupport ? handleMarkSupport : undefined,
				actionDisabled: actionLoading || !canMarkSupport,
				actionLoading,
			},
		};

		return mappedOrderDetail;
	}, [
		actionLoading,
		apiOrder,
		handleClaimWarranty,
		handleMarkSupport,
		handlePay,
		handleRetryPaymentRegistration,
		isClient,
		isEndUser,
		shouldHideProviderContactAndChat,
		handleOpenChat,
		latestRejectedPayment,
		nowTimestamp,
		requestScheduleOverride.date,
		requestScheduleOverride.hour,
	]);

	if (loading) {
		return (
			<Layout>
				<div className='flex min-h-[calc(100vh-160px)] items-center justify-center bg-slate-50'>
					<div className='h-12 w-12 animate-spin rounded-full border-b-2 border-primary' />
				</div>
			</Layout>
		);
	}

	if (isProvider && apiOrder && !providerOwnsOrder) {
		return (
			<Layout>
				<div className='flex min-h-[calc(100vh-160px)] items-center justify-center bg-slate-50 px-6'>
					<div className='w-full max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center'>
						<h2 className='text-base font-semibold text-amber-800'>
							Acceso restringido
						</h2>
						<p className='mt-2 text-sm text-amber-700'>
							Solo puedes ver la información de tus propios pedidos.
						</p>
						<button
							type='button'
							onClick={() =>
								navigate("/service/profile", { replace: true })
							}
							className='mt-4 inline-flex cursor-pointer items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover'
						>
							Ir a mis pedidos
						</button>
					</div>
				</div>
			</Layout>
		);
	}

	if (!hasValidOrderId || error || !orderDetail) {
		return (
			<Layout>
				<div className='flex min-h-[calc(100vh-160px)] items-center justify-center bg-slate-50'>
					<p className='font-medium text-red-500'>
						{!hasValidOrderId
							? "ID de pedido invalido o no encontrado."
							: "Error al cargar la informacion del pedido."}
					</p>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className='min-h-[calc(100vh-160px)] bg-slate-50'>
				<div className='mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6 sm:py-10'>
					<button
						type='button'
						onClick={handleGoBack}
						className='mb-2 cursor-pointer text-sm font-semibold text-slate-700 transition hover:text-primary sm:mb-4'
					>
						&lt; Volver a mis ordenes
					</button>

					{actionError && (
						<div className='rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
							{actionError.message ||
								"No se pudo procesar la accion del pedido."}
						</div>
					)}

					{latestRejectedPayment && (
						<div className='rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
							Tu conciliación de pago fue rechazada.
							{latestRejectedPayment.rejection_reason_payment
								? ` Motivo: ${latestRejectedPayment.rejection_reason_payment}`
								: ""}{" "}
							Debes volver a registrar el pago para continuar con el
							servicio.
						</div>
					)}

					<OrderTitleBar
						title={orderDetail.title}
						orderCode={orderDetail.orderCode}
						statusBadge={orderDetail.statusBadge}
					/>

					<StatusTimeline steps={orderDetail.statusSteps} />

					{orderDetail.quotation && (
						<section className='rounded-2xl bg-white p-4 soft-border space-y-4 sm:p-6'>
							<div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
								<p className='text-sm font-semibold text-gray-700'>
									Última Cotización{" "}
									<span className='font-normal text-gray-400'>
										#{orderDetail.quotation.id}
									</span>
								</p>
								<span
									className={`inline-flex w-fit self-start items-center rounded-full px-3 py-1 text-xs font-semibold ${
										orderDetail.quotation.accepted
											? "bg-emerald-100 text-emerald-700"
											: "bg-amber-100 text-amber-700"
									}`}
								>
									{orderDetail.quotation.accepted
										? "Aceptada"
										: "Pendiente de aceptación"}
								</span>
							</div>

							{orderDetail.quotation.items.length > 0 && (
								<ul className='space-y-2 text-sm'>
									{orderDetail.quotation.items.map((item, i) => (
										<li
											key={i}
											className='flex items-start justify-between gap-3 text-gray-700'
										>
											<span className='text-gray-500'>
												{item.description}
											</span>
											<span className='font-medium whitespace-nowrap'>
												${item.subtotal}
											</span>
										</li>
									))}
								</ul>
							)}

							<div className='h-px bg-gray-100' />

							<div className='flex items-center justify-between text-sm font-semibold text-gray-800'>
								<span>Total cotizado</span>
								<span>${orderDetail.quotation.proposedPrice}</span>
							</div>
						</section>
					)}

					<div className='grid gap-6 lg:grid-cols-[2fr_1fr] lg:gap-x-4'>
						<div className='space-y-6 min-w-0 lg:pr-0'>
							{/* Detalles del Pedido */}
							<section className='rounded-2xl bg-white p-4 soft-border space-y-4 sm:p-6'>
								<p className='text-sm font-semibold text-gray-700'>
									Detalles del Pedido
								</p>

								<div className='space-y-2 text-sm'>
									{orderDetail.service.servicio && (
										<div>
											<p className='text-gray-400'>Servicio</p>
											<p className='font-medium text-gray-800'>
												{orderDetail.service.servicio}
											</p>
										</div>
									)}
									<div className='flex flex-wrap items-center gap-2'>
										<span className='inline-flex items-center rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-primary'>
											{orderDetail.service.categoria}
										</span>
									</div>
									{orderDetail.service.descripcion && (
										<div>
											<p className='text-gray-400'>Descripcion</p>
											<p className='leading-relaxed text-gray-700'>
												{orderDetail.service.descripcion}
											</p>
										</div>
									)}
								</div>

								<div className='h-px bg-gray-100' />

								<div className='space-y-3 text-sm'>
									{orderDetail.meta.map((entry, i) => (
										<div key={entry.label + i}>
											{entry.separator && (
												<div className='h-px bg-border my-3' />
											)}
											<div className='flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3'>
												<span className='text-gray-400'>
													{entry.label}
												</span>
												<span className='text-gray-800 sm:text-right'>
													{entry.value}
												</span>
											</div>
										</div>
									))}
								</div>
							</section>

							<ProviderCard
								provider={orderDetail.provider}
								showProfileLink={!isProvider}
							/>
							<GuaranteeCard guarantee={orderDetail.guarantee} />
							<NotesCard notes={orderDetail.notes} />
						</div>

						<div className='space-y-6 min-w-0 lg:w-full lg:pr-2'>
							<PaymentSummaryCard payment={orderDetail.payment} />

							{/* Pagos existentes con soporte */}
							{!isProvider &&
								apiOrder?.payments &&
								apiOrder.payments.length > 0 && (
									<aside className='rounded-2xl bg-white p-4 soft-border sm:p-6'>
										<p className='mb-4 text-sm font-semibold text-gray-700'>
											Comprobantes de Pago
										</p>
										<div className='space-y-3'>
											{apiOrder.payments.map((p, i) => (
												<div
													key={p.id}
													className='rounded-xl border border-slate-100 p-3 space-y-2'
												>
													<div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm'>
														<span className='font-medium text-gray-700'>
															Pago {i + 1}
														</span>
														<span
															className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
																p.payment_status ===
																"Aprobado"
																	? "bg-green-50 text-green-600"
																	: p.payment_status ===
																		  "Rechazado"
																		? "bg-red-50 text-red-600"
																		: "bg-amber-50 text-amber-600"
															}`}
														>
															{p.payment_status}
														</span>
													</div>
													<div className='text-xs text-gray-500 space-y-1'>
														<p>Monto: ${p.amount_payment}</p>
														<p>
															Desde: {p.from_account} →{" "}
															{p.to_account}
														</p>
														{p.methods?.name_method && (
															<p>
																Metodo:{" "}
																{p.methods.name_method}
															</p>
														)}
													</div>
													{p.payment_support && (
														<div className='mt-2'>
															<p className='text-xs text-gray-400 mb-1'>
																Comprobante:
															</p>
															{resolvePaymentSupportSrc(
																p.payment_support,
															) ? (
																<img
																	src={
																		resolvePaymentSupportSrc(
																			p.payment_support,
																		) ?? undefined
																	}
																	alt={`Comprobante pago ${i + 1}`}
																	className='max-h-40 rounded-lg border border-slate-200 object-contain'
																/>
															) : null}
														</div>
													)}
													{p.rejection_reason_payment && (
														<p className='text-xs text-red-500 mt-1'>
															Motivo de rechazo:{" "}
															{p.rejection_reason_payment}
														</p>
													)}
												</div>
											))}
										</div>
									</aside>
								)}

							<SupportCard help={orderDetail.help} />
						</div>
					</div>
				</div>
			</div>

			<ReportReasonModal
				open={showReportModal}
				reason={reportReason}
				error={reportReasonError}
				loading={actionLoading}
				onReasonChange={(value) => {
					setReportReason(value);
					if (reportReasonError) setReportReasonError("");
				}}
				onClose={handleCloseReportModal}
				onSubmit={() => {
					void handleSubmitSupportReport();
				}}
			/>

			{/* Payment Registration Modal */}
			{showPaymentForm && (
				<div className='fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6 sm:items-center sm:px-0'>
					<div className='relative w-full max-w-lg rounded-2xl bg-white p-4 shadow-2xl sm:mx-4 sm:p-6'>
						<button
							type='button'
							aria-label='Cerrar formulario de pago'
							onClick={() => setShowPaymentForm(false)}
							className='absolute right-4 top-4 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600'
						>
							<X className='h-5 w-5' />
						</button>

						<h2 className='mb-1 text-lg font-semibold text-gray-800'>
							Registrar Pago
						</h2>
						<p className='mb-5 text-sm text-gray-500'>
							Completa los datos de tu pago y adjunta la captura de pantalla
							del comprobante.
						</p>

						{exchangeRateData && (
							<div className='mb-4 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700'>
								Tasa de cambio BCV: 1 USD = {exchangeRateData.usd_to_bs}{" "}
								Bs
							</div>
						)}

						<div className='space-y-4'>
							{/* Metodo de pago */}
							<div>
								<label
									htmlFor='payment-method-select'
									className='mb-1 block text-sm font-medium text-gray-700'
								>
									Metodo de pago
								</label>
								<select
									id='payment-method-select'
									value={selectedMethodId ?? ""}
									onChange={(e) =>
										setSelectedMethodId(
											e.target.value
												? Number(e.target.value)
												: null,
										)
									}
									className='w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-primary focus:ring-1 focus:ring-primary'
								>
									<option value=''>Selecciona un metodo</option>
									{methods.map((m) => (
										<option key={m.id} value={m.id}>
											{m.name_method}
										</option>
									))}
								</select>
							</div>

							{/* Monto */}
							<div>
								<label className='mb-1 block text-sm font-medium text-gray-700'>
									Monto (USD)
								</label>
								<input
									type='number'
									min='0'
									step='0.01'
									value={paymentAmount}
									onChange={(e) => setPaymentAmount(e.target.value)}
									placeholder='0.00'
									className='w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-primary focus:ring-1 focus:ring-primary'
								/>
							</div>

							{/* Cuenta origen */}
							<div>
								<label className='mb-1 block text-sm font-medium text-gray-700'>
									Cuenta de origen
								</label>
								<input
									type='text'
									value={fromAccount}
									onChange={(e) => setFromAccount(e.target.value)}
									placeholder='Tu numero de cuenta o referencia'
									className='w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-primary focus:ring-1 focus:ring-primary'
								/>
							</div>

							{/* Cuenta destino */}
							<div>
								<label
									htmlFor='to-account-select'
									className='mb-1 block text-sm font-medium text-gray-700'
								>
									Cuenta destino
								</label>
								{providerAccountsList.length > 0 ? (
									<select
										id='to-account-select'
										value={toAccount}
										onChange={(e) => setToAccount(e.target.value)}
										className='w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-primary focus:ring-1 focus:ring-primary'
									>
										<option value=''>
											Selecciona la cuenta del proveedor
										</option>
										{providerAccountsList.map((acc) => (
											<option
												key={acc.id}
												value={acc.account_number}
											>
												{acc.holder_name} - {acc.account_number}
											</option>
										))}
									</select>
								) : (
									<input
										type='text'
										value={toAccount}
										onChange={(e) => setToAccount(e.target.value)}
										placeholder='Cuenta del proveedor'
										className='w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-primary focus:ring-1 focus:ring-primary'
									/>
								)}
							</div>

							{/* Captura de pantalla */}
							<div>
								<label className='mb-1 block text-sm font-medium text-gray-700'>
									Captura de pantalla del comprobante
								</label>
								{screenshotPreview ? (
									<div className='relative'>
										<img
											src={screenshotPreview}
											alt='Comprobante de pago'
											className='max-h-48 w-full rounded-lg border border-gray-200 object-contain'
										/>
										<button
											type='button'
											aria-label='Eliminar captura de pantalla'
											onClick={clearScreenshot}
											className='absolute right-2 top-2 rounded-full bg-white/80 p-1 text-gray-500 shadow transition hover:bg-white hover:text-red-500'
										>
											<X className='h-4 w-4' />
										</button>
									</div>
								) : (
									<button
										type='button'
										onClick={() => fileInputRef.current?.click()}
										className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500 transition hover:border-primary hover:text-primary'
									>
										<Upload className='h-5 w-5' />
										<span>Subir captura de pantalla</span>
									</button>
								)}
								<input
									ref={fileInputRef}
									type='file'
									aria-label='Seleccionar captura de pantalla del comprobante'
									accept='image/jpeg,image/png,image/webp'
									onChange={handleScreenshotChange}
									className='hidden'
								/>
								<p className='mt-1 text-xs text-gray-400'>
									Formatos: JPG, PNG, WEBP. Max 5MB.
								</p>
							</div>
						</div>

						{paymentError && (
							<div className='mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600'>
								{paymentError}
							</div>
						)}

						<div className='mt-6 flex flex-col gap-3 sm:flex-row sm:items-center'>
							<button
								type='button'
								onClick={() => setShowPaymentForm(false)}
								disabled={creatingPayment}
								className='flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50'
							>
								Cancelar
							</button>
							<button
								type='button'
								onClick={handleSubmitPayment}
								disabled={creatingPayment}
								className='flex-1 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:opacity-50'
							>
								{creatingPayment ? "Registrando..." : "Registrar Pago"}
							</button>
						</div>
					</div>
				</div>
			)}
		</Layout>
	);
}
